import * as Api from '@ViewModels';
import { ISystemEmailTemplate } from '../../../../../models/AdminModels';
import { useEventLogging } from '../../../../../models/Logging';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../../../models/hooks/appStateHooks';
import {
	fetchHwcTemplate,
	invalidateHwcIndustryCategories,
	invalidateHwcIndustryCategoryTemplates,
	useHwcTemplateAddStatusesMutation,
	useHwcTemplateRemoveStatusesMutation,
} from '../../../../../queries';
import { KnownCategories } from '../../../../../viewmodels/AppViewModels';

export const useHwcTemplateActions = ({ industry }: { industry: Api.Industry }) => {
	const userSession = useUserSession();
	const fullscreenModal = useFullscreenModal();
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('HWCTemplateCategoryRow');
	const templateRemoveStatusesMutation = useHwcTemplateRemoveStatusesMutation({
		onError: error => {
			errorMessages.push({
				messages: ['Failed to perform operation'],
			});
			logApiError('RemoveStatuses-Error', error);
		},
		onSuccess: (_, variables) => {
			variables.statuses.forEach(status => {
				invalidateHwcIndustryCategoryTemplates({ categoryName: status.category, industry });
			});
			invalidateHwcIndustryCategories({ industry });
		},
	});
	const templateAddStatusesMutation = useHwcTemplateAddStatusesMutation({
		onError: error => {
			errorMessages.push({
				messages: ['Failed to perform operation'],
			});
			logApiError('AddStatuses-Error', error);
		},
		onSuccess: (_, variables) => {
			variables.statuses.forEach(status => {
				invalidateHwcIndustryCategoryTemplates({ categoryName: status.category, industry });
			});
			invalidateHwcIndustryCategories({ industry });
		},
	});

	const handleDeleteTemplate = async ({ template }: { template: ISystemEmailTemplate<Api.ITemplate> }) => {
		try {
			const templateWithStatuses = await fetchHwcTemplate({
				templateId: template.template.id,
				userSession,
			});
			templateRemoveStatusesMutation.mutate({
				statuses: templateWithStatuses.statuses,
				templateId: template.template.id,
			});
		} catch (err) {
			logApiError('DeleteTemplate-Error', err);
			errorMessages.pushApiError(err);
		}
	};
	const handleArchiveTemplate = async ({ template }: { template: ISystemEmailTemplate<Api.ITemplate> }) => {
		try {
			const templateWithStatuses = await fetchHwcTemplate({
				templateId: template.template.id,
				userSession,
			});
			const statusesToAdd: Api.ITemplateStatus[] = Object.keys(template.industries).map(_industry => ({
				category: 'Archived',
				industry: _industry as Api.Industry,
				status: Api.TemplateStatus.Archived,
			}));
			await templateAddStatusesMutation.mutateAsync({
				statuses: statusesToAdd,
				templateId: template.template.id,
			});
			await templateRemoveStatusesMutation.mutateAsync({
				statuses: templateWithStatuses.statuses,
				templateId: template.template.id,
			});
		} catch (err) {
			logApiError('ArchiveTemplate-Error', err);
			errorMessages.pushApiError(err);
		}
	};
	const handleFeatureTemplate = ({ template }: { template: ISystemEmailTemplate<Api.ITemplate> }) => {
		templateAddStatusesMutation.mutate({
			statuses: [
				{
					category: KnownCategories.Featured,
					industry,
					status: Api.TemplateStatus.Published,
				},
			],
			templateId: template.template.id,
		});
	};
	const handleUnfeatureTemplate = ({
		template,
		category,
	}: {
		template: ISystemEmailTemplate<Api.ITemplate>;
		category: Api.ITemplateCategory;
	}) => {
		templateRemoveStatusesMutation.mutate({
			statuses: [{ category: category.name, industry }],
			templateId: template.template.id,
		});
	};
	const handleViewTemplate = ({ template }: { template: ISystemEmailTemplate<Api.ITemplate> }) => {
		fullscreenModal.history.push({
			pathname: `/campaign-templates/hwc/view/${template.template.id}`,
		});
	};
	const handleEditTemplate = ({ template }: { template: ISystemEmailTemplate<Api.ITemplate> }) => {
		fullscreenModal.history.push({
			pathname: `/campaign-templates/hwc/edit/${template.template.id}`,
		});
	};

	return {
		templateAddStatusesMutation,
		templateRemoveStatusesMutation,
		handleDeleteTemplate,
		handleArchiveTemplate,
		handleFeatureTemplate,
		handleUnfeatureTemplate,
		handleViewTemplate,
		handleEditTemplate,
	};
};
